var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { ArrowUpward } from '@mui/icons-material';
import { Box, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material';
import { setTemplateSelectedItem, setTemplateSortSelectedItem } from 'app/actions/checklist';
import { Autosizer, Scrollbar } from 'app/components';
import React, { createContext, forwardRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const ChecklistTemplateListContext = createContext(null);
const ChecklistTemplateList = () => {
    const dispatch = useDispatch();
    const { templateItems, templateSelectedItem, templateSearchValue, templateSortSelectedItem } = useSelector((state) => state.checklist);
    const sortItems = useMemo(() => {
        const compareFn = (a, b) => {
            switch (templateSortSelectedItem.name) {
                case 'created_at':
                case 'updated_at': {
                    const sortKey = templateSortSelectedItem.name;
                    switch (templateSortSelectedItem.order) {
                        case 'ASC':
                            return new Date(String(a[sortKey])).getTime() - new Date(String(b[sortKey])).getTime();
                        case 'DESC':
                            return new Date(String(b[sortKey])).getTime() - new Date(String(a[sortKey])).getTime();
                    }
                    break;
                }
                case 'name':
                    switch (templateSortSelectedItem.order) {
                        case 'ASC':
                            return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
                        case 'DESC':
                            return b.name.localeCompare(a.name, undefined, { sensitivity: 'base' });
                    }
                    break;
                case 'active':
                    switch (templateSortSelectedItem.order) {
                        case 'ASC':
                            return Number(a.active) - Number(b.active);
                        case 'DESC':
                            return Number(b.active) - Number(a.active);
                        default:
                            break;
                    }
                    break;
            }
        };
        if (templateSearchValue.length) {
            return templateItems
                .filter((item) => item.name.toLowerCase().indexOf(templateSearchValue.toLowerCase()) !== -1)
                .sort(compareFn);
        }
        return templateItems.sort(compareFn);
    }, [templateItems, templateSelectedItem, templateSearchValue, templateSortSelectedItem]);
    const handleItemClick = (value) => () => dispatch(setTemplateSelectedItem(value));
    const handleSortClick = (value) => () => dispatch(setTemplateSortSelectedItem(value));
    return (React.createElement(Box, { component: 'div', sx: (theme) => ({
            width: '100%',
            height: '100%',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
            '.simplebar-horizontal': {
                right: '10px',
            },
            '.simplebar-vertical': {
                top: '30px',
                bottom: '0',
            },
            '.MuiListItem-root': {
                width: '100%',
                userSelect: 'none',
                padding: theme.spacing(0),
                borderRadius: '0',
            },
        }) },
        React.createElement(Autosizer, null, ({ height, width }) => (React.createElement(Box, { sx: { height, width } },
            React.createElement(ChecklistTemplateListContext.Provider, { value: {
                    sortItemClick: handleSortClick,
                    sortSelectedItem: templateSortSelectedItem,
                } },
                React.createElement(Scrollbar, null, ({ scrollableNodeRef, contentNodeRef }) => (React.createElement(FixedSizeList, { height: height, itemSize: 24, overscanCount: 10, itemCount: sortItems.length, innerRef: contentNodeRef, outerRef: scrollableNodeRef, innerElementType: TemplateListInnerElementType, itemData: {
                        items: sortItems,
                        selectedItem: templateSelectedItem,
                        onClick: handleItemClick,
                    } }, TemplateListItemRender)))))))));
};
const TemplateListInnerElementType = forwardRef((_a, ref) => {
    var { children, style } = _a, rest = __rest(_a, ["children", "style"]);
    return (React.createElement(ChecklistTemplateListContext.Consumer, null, (context) => {
        if (!context) {
            return null;
        }
        const { sortItemClick, sortSelectedItem } = context;
        return (React.createElement(List, Object.assign({ ref: ref, disablePadding: true, sx: {
                height: style === null || style === void 0 ? void 0 : style.height,
                minHeight: '100%',
            } }, rest),
            React.createElement(ListItem, { sx: (theme) => ({
                    position: 'absolute',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: style === null || style === void 0 ? void 0 : style.height,
                    minHeight: '100%',
                    zIndex: '2',
                    pointerEvents: 'none',
                    padding: theme.spacing(0),
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        height: '100%',
                        position: 'relative',
                        flex: 'none',
                        '&:before': {
                            content: '""',
                            position: 'absolute',
                            top: '0',
                            right: '0',
                            height: '100%',
                            bgcolor: theme.palette.divider,
                            width: '1px',
                            zIndex: '1',
                            pointerEvents: 'none',
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '&:before': {
                                display: 'none',
                            },
                        },
                    },
                }) }, config.checklistTemplateColumns.map((column) => (React.createElement(ListItemText, { key: column.name, style: { width: column.width } })))),
            React.createElement(ListItem, { disablePadding: true, sx: (theme) => ({
                    width: '100%',
                    display: 'flex',
                    position: 'sticky !important',
                    top: '0',
                    zIndex: '1',
                    '.MuiListItemButton-root': {
                        display: 'flex',
                        flex: 'none',
                        padding: theme.spacing(0),
                        position: 'relative',
                        bgcolor: 'background.default',
                        borderColor: theme.palette.divider,
                        '&:first-of-type': {
                            '.MuiListItemText-root': {
                                paddingLeft: theme.spacing(2),
                            },
                        },
                        '&:last-of-type': {
                            flex: '1',
                            '.MuiListItemText-root': {
                                paddingRight: theme.spacing(2),
                            },
                        },
                        '.MuiTouchRipple-rippleVisible': {
                            color: 'inherit',
                        },
                        '.MuiListItemText-root': {
                            display: 'flex',
                            cursor: 'pointer',
                            margin: theme.spacing(0),
                            padding: '6px 6px',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            '.MuiTypography-root': {
                                display: 'flex',
                                '&:nth-of-type(1)': {
                                    color: 'text.secondary',
                                    marginRight: '4px',
                                },
                                '&:nth-of-type(2) svg': {
                                    fontSize: '12px',
                                    transition: 'all 300ms',
                                    transform: 'rotateZ(0deg)',
                                },
                            },
                        },
                    },
                }) }, config.checklistTemplateColumns.map((column) => (React.createElement(ListItemButton, { key: column.name, onClick: sortItemClick(column), disableTouchRipple: !column.order, sx: (theme) => ({
                    minWidth: column.width,
                    '.MuiListItemText-root': {
                        display: 'flex',
                        justifyContent: 'space-between',
                        '.MuiTypography-root': {
                            width: 'auto',
                            '&:nth-of-type(2)': Object.assign({ opacity: '0.2', transform: 'rotateZ(0)', transition: 'transform ' + theme.transitions.duration.shortest + 'ms ' + theme.transitions.easing.easeInOut + ' 0ms' }, (sortSelectedItem.name === column.name && Object.assign({ opacity: '1' }, (sortSelectedItem.order === 'DESC' && {
                                transform: 'rotateZ(180deg)',
                            })))),
                        },
                    },
                }) },
                React.createElement(ListItemText, { disableTypography: true },
                    React.createElement(Typography, null, column.label),
                    React.createElement(Typography, null, column.order && React.createElement(ArrowUpward, { fontSize: 'small' }))))))),
            children));
    }));
});
TemplateListInnerElementType.displayName = 'TemplateListInnerElementType';
const TemplateListItemRender = ({ data, index, style }) => {
    const { items, selectedItem, onClick } = data;
    const item = items[index];
    return (React.createElement(ListItem, { key: index, disablePadding: true, style: style, selected: item.id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id), onClick: onClick(item), sx: (theme) => (Object.assign({ width: '100%', marginTop: '30px', userSelect: 'none', borderRadius: '0', '&:hover': {
                '.MuiListItemText-root': {
                    cursor: 'pointer',
                },
            }, '&.Mui-selected': {
                '.MuiListItemText-root': {
                    bgcolor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                },
                '.MuiCheckbox-root': {
                    color: theme.palette.primary.contrastText,
                    '&.Mui-checked': {
                        color: theme.palette.primary.contrastText,
                    },
                },
            }, '.MuiListItemText-root': {
                display: 'flex',
                flex: 'none',
                height: '100%',
                margin: theme.spacing(0),
                padding: theme.spacing(0.5, 1),
                alignItems: 'center',
                '&:first-of-type': {
                    paddingLeft: theme.spacing(2),
                    svg: {
                        fontSize: '16px',
                        marginRight: '3px',
                    },
                },
                '&:last-of-type': {
                    flex: '1',
                    paddingRight: theme.spacing(2),
                },
                '.MuiTypography-root': {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                    boxSizing: 'border-box',
                },
                '.MuiCheckbox-root': {
                    padding: theme.spacing(0),
                    marginLeft: '-2px',
                    svg: {
                        fontSize: '18px',
                    },
                },
            } }, ((items.length - (items.length - 1 - index)) % 2 === 0 && {
            '& .MuiListItemText-root': {
                bgcolor: 'background.default',
            },
        }))) }, config.checklistTemplateColumns.map((column) => {
        var _a;
        return (React.createElement(ListItemText, { key: column.name, sx: { width: column.width } }, React.createElement(Typography, { noWrap: true }, column.format ? column.format(item[column.name]) : String((_a = item[column.name]) !== null && _a !== void 0 ? _a : ''))));
    })));
};
export default ChecklistTemplateList;
